<script setup>
// Imports
import { is } from 'quasar'

// Props
const props = defineProps({
  customSelectAllText: {
    type: undefined,
    required: false,
    default: undefined,
  },
  options: {
    type: undefined,
    required: false,
    default: undefined,
  },
  itemKey: {
    type: undefined,
    required: false,
    default: undefined,
  },
  itemsService: {
    type: undefined,
    required: false,
    default: undefined,
  },
  translateFilterOptions: {
    type: undefined,
    required: false,
    default: undefined,
  },
  modelValue: {
    type: undefined,
    required: false,
    default: undefined,
  },
  searchableSelect: {
    type: Boolean,
    required: false,
    default: false,
  },
  clearable: {
    type: Boolean,
    required: false,
    default: false,
  },
  label: {
    type: undefined,
    required: false,
    default: undefined,
  },
  placeholder: {
    type: undefined,
    required: false,
    default: undefined,
  },
  error: {
    type: undefined,
    required: false,
    default: undefined,
  },
  errorMessage: {
    type: undefined,
    required: false,
    default: undefined,
  },
  multiple: {
    type: Boolean,
    required: false,
    default: false,
  },
  selectAllFiltered: {
    type: Boolean,
    default: false,
  },
  emitValue: {
    type: Boolean,
    default: true,
  },
  mapOptions: {
    type: Boolean,
    default: true,
  },
  optionValue: {
    type: [String, Function],
    default: undefined,
  },
  optionLabel: {
    type: [String, Function],
    default: undefined,
  },
  optionDisable: {
    type: [String, Function],
    default: undefined,
  },
  disable: {
    type: Boolean,
    default: false,
  },
  optionsConfig: {
    type: undefined,
    required: false,
    default: undefined,
  },
  readonly: {
    type: undefined,
    required: false,
    default: undefined,
  },
})

// Emits
const emit = defineEmits(['update:modelValue', 'fieldError'])

// Refs
const selector = ref()
const searchVal = ref('')
const isFocused = ref(false)
const filterModel = ref(props.modelValue ?? (props.multiple ? [] : null))

// Computed
const options = computed(() => (Array.isArray(props.options) ? props.options : []))
const foundOptions = computed(() => {
  const needle = searchVal.value.toLowerCase()

  if (!needle) {
    return options.value
  }

  const filteredOptions = []
  let header = null
  let headerMatches = false
  let headerAdded = false

  for (const option of options.value) {
    const matches = [optionLabel(option), option?.prefix, option?.caption].some((value) =>
      String(value ?? '')
        .toLowerCase()
        .includes(needle),
    )

    if (option?.type === 'header') {
      header = option
      headerMatches = matches
      headerAdded = false
      continue
    }

    if (matches || headerMatches) {
      if (header && !headerAdded) {
        filteredOptions.push(header)
        headerAdded = true
      }
      filteredOptions.push(option)
    }
  }

  return filteredOptions
})
const selectableOptions = computed(() => (props.selectAllFiltered ? foundOptions.value : options.value).filter((option) => !optionDisabled(option)))
const visibleOptionCount = computed(() => foundOptions.value.filter((option) => !optionDisabled(option)).length)
const selectedValues = computed(() => new Set((Array.isArray(filterModel.value) ? filterModel.value : []).map(modelOptionValue)))
const all = computed({
  get() {
    if (!props.multiple || !selectableOptions.value.length) return false

    const count = selectableOptions.value.filter((option) => hasValue(selectedValues.value, optionValue(option))).length
    return count === selectableOptions.value.length ? true : props.selectAllFiltered && count > 0 ? null : false
  },
  set(checked) {
    if (!props.multiple || props.readonly || props.disable || !selectableOptions.value.length) return

    const values = selectableOptions.value.map((option) => (props.emitValue ? optionValue(option) : option))
    if (!props.selectAllFiltered) {
      updateValue(checked ? values : [])
      return
    }

    const selected = Array.isArray(filterModel.value) ? filterModel.value : []
    const visibleValues = new Set(selectableOptions.value.map(optionValue))
    updateValue(
      checked
        ? [...selected, ...values.filter((value) => !hasValue(selectedValues.value, modelOptionValue(value)))]
        : selected.filter((value) => !hasValue(visibleValues, modelOptionValue(value))),
    )
  },
})
const massSelectText = computed(() => {
  return all.value !== true ? (props.customSelectAllText ? props.customSelectAllText : 'Shared.Globals.Select All') : 'Shared.Globals.Deselect All'
})

// Methods
// Match QSelect's property access, including primitive options and custom getter functions.
const optionProperty = (option, property, fallback) => {
  if (typeof property === 'function') return property(option)
  const key = property ?? fallback
  return option !== null && typeof option === 'object' && key in option ? option[key] : option
}
const optionValue = (option) => optionProperty(option, props.optionValue, 'value')
const modelOptionValue = (value) => (props.emitValue ? value : optionValue(value))
const optionLabel = (option) => optionProperty(option, props.optionLabel, 'label')
const optionDisabled = (option) =>
  option?.type === 'header' || !!option?.disabled || !!option?.disable || optionProperty(option, props.optionDisable, 'disable') === true
const hasValue = (values, value) =>
  values.has(value) || (value !== null && typeof value === 'object' && [...values].some((selected) => is.deepEqual(selected, value)))
const displayLabel = (option) => {
  if (option?.label !== undefined && props.optionLabel === undefined) return option.label
  if (props.optionsConfig?.labelKey && option?.value != null && props.optionLabel === undefined) return option.value[props.optionsConfig.labelKey]
  return optionLabel(option)
}

/**
 * Search in the options
 * @param val
 * @param update
 */
const filterFn = (val, update) => {
  searchVal.value = val ?? ''
  update()
}
/**
 * When the element is focussed, enable search input (when the field should be searchable according the schema)
 * @param state
 */
const setFocus = async (state) => {
  const shouldFocus = !!state
  isFocused.value = shouldFocus

  if (!shouldFocus) {
    await selector.value?.updateInputValue('', true)
    searchVal.value = ''
  }

  await nextTick()

  selector.value?.[shouldFocus ? 'focus' : 'blur']()
}

/**
 * Triggers an emit on selection change
 * @param value
 */
const updateValue = (value) => {
  filterModel.value = value ?? (props.multiple ? [] : null)
  emit('update:modelValue', value)
}

// Watchers
watch(
  () => props.modelValue,
  (value) => {
    filterModel.value = value ?? (props.multiple ? [] : null)
  },
)

/**
 * If an element has an error, emit this to parent component
 */
watchEffect(() => {
  if (selector?.value?.hasError) {
    emit('fieldError', props.itemKey)
  }
})

// Expose
defineExpose({ isFocused, visibleOptionCount })
</script>

<template>
  <q-select
    ref="selector"
    class="non-selectable"
    :clearable="clearable"
    :use-input="searchableSelect && isFocused"
    input-debounce="0"
    outlined
    :model-value="filterModel"
    :emit-value="emitValue"
    :map-options="mapOptions"
    :option-value="props.optionValue"
    :option-label="props.optionLabel"
    :option-disable="optionDisabled"
    :options="foundOptions"
    :label="label"
    :placeholder="placeholder"
    :error="error"
    :error-message="errorMessage"
    :multiple="multiple"
    :readonly="readonly"
    :disable="disable"
    :use-chips="multiple"
    :hide-selected="searchableSelect && isFocused"
    v-bind="$attrs"
    @filter="filterFn"
    @popup-show="setFocus(true)"
    @popup-hide="setFocus(false)"
    @update:model-value="updateValue"
  >
    <!-- Bulk selection is scoped to all options unless filtered selection is explicitly enabled -->
    <template v-if="multiple" #before-options>
      <q-item class="sticky-header non-selectable" :class="{ 'q-item--active': all }" clickable dense>
        <q-item-section side>
          <q-checkbox v-model="all" :disable="readonly || disable || !selectableOptions.length" :indeterminate-value="null"></q-checkbox>
        </q-item-section>
        <q-item-section>
          <q-item-label @click="all = all !== true">{{ $t(massSelectText) }}</q-item-label>
        </q-item-section>
      </q-item>
    </template>
    <!-- Default options; consumers can replace this slot -->
    <template #option="{ itemProps, opt, selected, toggleOption }">
      <q-item
        class="non-selectable"
        v-bind="itemProps"
        dense
        :disable="readonly || disable || optionDisabled(opt)"
        :class="{
          'text-black cursor-pointer': opt?.disabled,
          'no-padding': opt?.disabled && multiple,
        }"
      >
        <q-item-section v-if="multiple" side>
          <q-checkbox
            v-if="opt?.type !== 'header'"
            :model-value="selected"
            :disable="readonly || disable || optionDisabled(opt)"
            @update:model-value="toggleOption(opt)"
          />
        </q-item-section>
        <q-item-section>
          <q-item-label v-if="opt?.caption" :class="opt.class || undefined"
            ><strong>{{ opt.caption }}</strong></q-item-label
          >
          <q-item-label :class="{ 'text-grey small-text line-breaks': opt?.caption }"
            >{{ opt?.prefix ? opt.prefix + ' - ' : '' }}{{ displayLabel(opt) }}
          </q-item-label>
          <q-tooltip v-if="opt?.tooltip" :delay="500" class="line-breaks">{{ opt.tooltip }}</q-tooltip>
        </q-item-section>
      </q-item>
    </template>
    <template #no-option>
      <q-item class="sticky-header non-selectable" dense>
        <q-item-section>
          <q-item-label>{{ $t('Shared.Globals.Errors.No options available') }}</q-item-label>
        </q-item-section>
      </q-item>
    </template>
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData || {}" class="non-selectable"></slot>
    </template>
  </q-select>
</template>

<style scoped></style>
